import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContract, fetchContractSetupInfo, waiveContract } from '../lib/bridge';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlClass, fieldLabelClass } from '../components/ui/forms';

const PAYMENT_PLANS = ['50% Start / 50% Completion', '25% Start / 25% at Halfway / 50% Completion', 'Due Upon Completion', 'Custom'];

// Contract builder — Owner/Admin/Manager/Estimator only (JobDetailPage
// gates rendering to CONTRACT_CREATE_ROLES). Goes straight through the
// unmodified appCreateContract/appMarkContractNotRequired — see the
// comment on `contractCreate` in src/V6Api.js. Signing itself stays the
// existing customer-facing ?mode=sign link; this panel only creates the
// contract and surfaces that link for the employee to share.
export function ContractPanel({ documentNo, contractNo, contractStatus, booked }: { documentNo: string; contractNo?: string; contractStatus?: string; booked: boolean }) {
  const queryClient = useQueryClient();
  const setupQuery = useQuery({ queryKey: ['contractSetup'], queryFn: fetchContractSetupInfo });
  const [showForm, setShowForm] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [paymentPlan, setPaymentPlan] = useState(PAYMENT_PLANS[0]);
  const [customTerms, setCustomTerms] = useState('');
  const [materials, setMaterials] = useState('');
  const [customerItems, setCustomerItems] = useState('None');
  const [cleanup, setCleanup] = useState('Included');
  const [materialSpecs, setMaterialSpecs] = useState('');
  const [unforeseen, setUnforeseen] = useState('Do Not Include');
  const [specialOrder, setSpecialOrder] = useState('Do Not Include');
  const [internalTestAcknowledged, setInternalTestAcknowledged] = useState(false);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['jobDetail'] });
  }

  const createMutation = useMutation({
    mutationFn: () =>
      createContract({
        documentNo,
        startDate,
        completionDate,
        paymentPlan,
        customTerms,
        materials,
        customerItems,
        cleanup,
        materialSpecs,
        unforeseen,
        specialOrder,
        internalTestAcknowledged,
      }),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const viewLinkMutation = useMutation({
    mutationFn: () => createContract({ documentNo, startDate: '', completionDate: '', paymentPlan: '', materials: '' }),
  });

  const waiveMutation = useMutation({
    mutationFn: () => waiveContract(documentNo),
    onSuccess: (result) => result.ok && invalidate(),
  });

  if (contractStatus === 'Not Required') {
    return (
      <Card>
        <CardHeader title="Contract" />
        <p className="text-sm text-slate-500">Not required for this job.</p>
      </Card>
    );
  }

  if (contractNo) {
    return (
      <Card>
        <CardHeader title="Contract" subtitle={`${contractNo} — ${contractStatus || 'Ready for Signature'}`} />
        <Button variant="secondary" busy={viewLinkMutation.isPending} busyLabel="Loading…" onClick={() => viewLinkMutation.mutate()}>
          Get Sign Link
        </Button>
        {viewLinkMutation.data?.ok && viewLinkMutation.data.signUrl && (
          <p className="mt-3 break-all rounded-lg border border-ink-border bg-ink p-2.5 text-sm text-slate-300">{viewLinkMutation.data.signUrl}</p>
        )}
        {viewLinkMutation.data && !viewLinkMutation.data.ok && <ErrorState message={viewLinkMutation.data.message || 'Could not load the sign link.'} />}
      </Card>
    );
  }

  if (!booked) {
    return (
      <Card>
        <CardHeader title="Contract" />
        <p className="text-sm text-slate-500">Book the job before creating a contract.</p>
      </Card>
    );
  }

  const setup = setupQuery.data?.setup;

  return (
    <Card>
      <CardHeader title="Contract" />

      {setup && setup.operationalMissing.length > 0 && (
        <ErrorState message={`Contract setup is incomplete: ${setup.operationalMissing.join(', ')}.`} />
      )}

      {!showForm ? (
        <div className="mt-3 flex gap-2">
          <Button onClick={() => setShowForm(true)}>Create Contract</Button>
          <Button variant="secondary" busy={waiveMutation.isPending} busyLabel="Saving…" onClick={() => waiveMutation.mutate()}>
            Mark Not Required
          </Button>
        </div>
      ) : (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          {setup?.internalTest && (
            <label className="flex items-start gap-2 rounded-lg border border-gold-800 bg-gold-900/20 p-2.5 text-sm text-gold-100">
              <input type="checkbox" checked={internalTestAcknowledged} onChange={(e) => setInternalTestAcknowledged(e.target.checked)} className="mt-0.5" />
              This contract is for internal testing only, not customer-ready. I understand.
            </label>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="ct-start" className={fieldLabelClass}>
                Start Date
              </label>
              <input id="ct-start" type="date" className={fieldControlClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <label htmlFor="ct-completion" className={fieldLabelClass}>
                Expected Completion
              </label>
              <input
                id="ct-completion"
                type="date"
                className={fieldControlClass}
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label htmlFor="ct-plan" className={fieldLabelClass}>
                Payment Plan
              </label>
              <select id="ct-plan" className={fieldControlClass} value={paymentPlan} onChange={(e) => setPaymentPlan(e.target.value)}>
                {PAYMENT_PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            {paymentPlan === 'Custom' && (
              <div className="space-y-1 sm:col-span-2">
                <label htmlFor="ct-custom" className={fieldLabelClass}>
                  Custom Payment Terms
                </label>
                <input id="ct-custom" className={fieldControlClass} value={customTerms} onChange={(e) => setCustomTerms(e.target.value)} required />
              </div>
            )}
            <div className="space-y-1 sm:col-span-2">
              <label htmlFor="ct-materials" className={fieldLabelClass}>
                Materials Responsibility
              </label>
              <input id="ct-materials" className={fieldControlClass} value={materials} onChange={(e) => setMaterials(e.target.value)} required />
            </div>
          </div>

          <button type="button" onClick={() => setShowMore((v) => !v)} className="text-sm font-semibold text-gold-300 hover:text-gold-200">
            {showMore ? 'Hide' : 'Show'} more terms
          </button>
          {showMore && (
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { id: 'ct-customer-items', label: 'Customer-Provided Items', value: customerItems, set: setCustomerItems },
                { id: 'ct-cleanup', label: 'Cleanup', value: cleanup, set: setCleanup },
                { id: 'ct-specs', label: 'Material Brands/Specs', value: materialSpecs, set: setMaterialSpecs },
                { id: 'ct-unforeseen', label: 'Unforeseen Conditions', value: unforeseen, set: setUnforeseen },
                { id: 'ct-special', label: 'Special-Order Items', value: specialOrder, set: setSpecialOrder },
              ].map((f) => (
                <div key={f.id} className="space-y-1">
                  <label htmlFor={f.id} className={fieldLabelClass}>
                    {f.label}
                  </label>
                  <input id={f.id} className={fieldControlClass} value={f.value} onChange={(e) => f.set(e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {createMutation.data && !createMutation.data.ok && <ErrorState message={createMutation.data.message || 'Could not create the contract.'} />}

          <div className="flex gap-2">
            <Button type="submit" busy={createMutation.isPending} busyLabel="Creating…">
              Create Contract
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {createMutation.data?.ok && createMutation.data.signUrl && (
        <p className="mt-3 break-all rounded-lg border border-success/30 bg-success-soft p-2.5 text-sm text-emerald-200">
          Contract created. Sign link: {createMutation.data.signUrl}
        </p>
      )}
      {waiveMutation.data && !waiveMutation.data.ok && <ErrorState message={waiveMutation.data.message || 'Could not mark this job as not requiring a contract.'} />}
    </Card>
  );
}
